import { Time } from "@commerce/components/time";
import { Link, useParams } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@ui/components/tooltip";
import { cn } from "@ui/lib";
import {
	CheckCheckIcon,
	CheckIcon,
	CircleAlertIcon,
	ClockIcon,
	HeadphonesIcon,
	type LucideIcon,
} from "lucide-react";
import type { ChannelMessageRow } from "./types";

/** Delivery states from least to most; a later webhook never moves a message back. */
const RANK = {
	held: 0,
	accepted: 1,
	sent: 2,
	delivered: 3,
	read: 4,
	played: 5,
} as const;
type Tick = keyof typeof RANK | "failed" | "skipped";

/**
 * The furthest a message got, from its status and every timestamp it collected. Statuses
 * arrive out of order (a `delivered` after a `read`), and the furthest one wins.
 */
export function deliveryState(
	row: Pick<
		ChannelMessageRow,
		"status" | "sentAt" | "deliveredAt" | "readAt" | "playedAt" | "failedAt"
	>,
): Tick {
	const status = row.status.toLowerCase();
	if (status === "failed" || (row.failedAt && !row.deliveredAt)) {
		return "failed";
	}
	if (status === "skipped") {
		return "skipped";
	}
	const seen: (keyof typeof RANK)[] = [
		...(status in RANK ? [status as keyof typeof RANK] : []),
		...(row.sentAt ? (["sent"] as const) : []),
		...(row.deliveredAt ? (["delivered"] as const) : []),
		...(row.readAt ? (["read"] as const) : []),
		...(row.playedAt ? (["played"] as const) : []),
	];
	return seen.reduce<keyof typeof RANK>(
		(best, next) => (RANK[next] > RANK[best] ? next : best),
		"held",
	);
}

const LABEL: Record<Tick, string> = {
	held: "Waiting to send",
	accepted: "Accepted by Meta",
	sent: "Sent",
	delivered: "Delivered",
	read: "Read",
	played: "Played",
	failed: "Not delivered",
	skipped: "Not sent",
};

/** The mark for each state a message reached; read and played are in the info colour. */
const TICK: Record<keyof typeof RANK, { icon: LucideIcon; seen: boolean }> = {
	held: { icon: ClockIcon, seen: false },
	accepted: { icon: ClockIcon, seen: false },
	sent: { icon: CheckIcon, seen: false },
	delivered: { icon: CheckCheckIcon, seen: false },
	read: { icon: CheckCheckIcon, seen: true },
	played: { icon: HeadphonesIcon, seen: true },
};

/**
 * WhatsApp's ticks for one message we sent: one for sent, two for delivered, two in the info
 * colour for read, headphones for a voice note played. A failed message links to its log.
 */
export function DeliveryTicks({
	row,
	className,
}: {
	row: ChannelMessageRow;
	className?: string;
}) {
	const state = deliveryState(row);

	if (state === "failed") {
		return <FailedTick row={row} className={className} />;
	}

	if (state === "skipped") {
		return (
			<span className={cn("text-muted-foreground text-xs", className)}>
				Not sent
			</span>
		);
	}

	const tick = TICK[state];
	const times = [
		["Sent", row.sentAt],
		["Delivered", row.deliveredAt],
		["Read", row.readAt],
		["Played", row.playedAt],
	] as const;

	return (
		<Tooltip>
			<TooltipTrigger
				render={
					<span
						role="img"
						aria-label={LABEL[state]}
						className={cn(
							"inline-flex items-center gap-1 text-muted-foreground",
							tick.seen && "text-info",
							className,
						)}
					/>
				}
			>
				<tick.icon className="size-3.5" aria-hidden />
			</TooltipTrigger>
			<TooltipContent>
				<span className="flex flex-col gap-0.5 tabular">
					<span className="font-medium">{LABEL[state]}</span>
					{times.map(([label, at]) =>
						at ? (
							<span key={label}>
								{label} <Time at={new Date(at)} />
							</span>
						) : null,
					)}
				</span>
			</TooltipContent>
		</Tooltip>
	);
}

/** A message Meta did not deliver: its error code, linking to its log when there is one. */
function FailedTick({
	row,
	className,
}: {
	row: ChannelMessageRow;
	className?: string;
}) {
	const { organizationSlug } = useParams({ strict: false });
	return (
		<Tooltip>
			<TooltipTrigger
				render={
					organizationSlug && row.wamid ? (
						<Link
							to="/app/$organizationSlug/logs"
							params={{ organizationSlug }}
							search={{ q: row.wamid }}
							aria-label="Not delivered. Open the log"
						/>
					) : (
						<span />
					)
				}
			>
				<Badge variant="destructive" className={className}>
					<CircleAlertIcon aria-hidden />
					{row.errorCode ? `Failed · ${row.errorCode}` : "Failed"}
				</Badge>
			</TooltipTrigger>
			<TooltipContent className="max-w-64">
				{row.errorTitle ?? "Meta could not deliver this message."} Open
				the log for what to do.
			</TooltipContent>
		</Tooltip>
	);
}
